import type { TradeOrder, TradingAccount } from './accountTypes';

/**
 * 계좌 화면의 표시 규칙. (12단계)
 *
 * 화면 컴포넌트에서 빼낸 이유
 *   "이 주문 상태를 무슨 색으로 보여줄 것인가", "이 스냅샷이 며칠 전인가" 는
 *   규칙이지 표시가 아니다. 컴포넌트 안에 두면 렌더링 테스트 없이 검증할 수 없는데,
 *   체결된 주문이 취소로 보이는 종류의 버그는 눈으로 잘 안 잡힌다.
 */

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/**
 * 계좌들 가운데 가장 늦게 동기화된 시각. 계좌가 없으면 null (2026-10-16, docs/impl/150 1번).
 * 배치와 화면 요청 둘 다 syncedAt 을 고치므로 "이 화면 숫자가 언제 토스 값인가" 의 답이다.
 * 'YYYY-MM-DDTHH:mm:ss' 문자열이라 사전 순서가 곧 시각 순서다.
 */
export function latestSyncedAt(accounts: TradingAccount[]): string | null {
  return accounts.map((account) => account.syncedAt).sort().at(-1) ?? null;
}

/**
 * 보유 종목 기준선 한 줄 (2026-10-16, docs/impl/150 5번).
 * 예) "6개월 범위 35% · 3개월 52% · ★ 1단 45,000원 · 모의 저점매수 감시 중(매수 기준가 44,000원 · 손절선 41,000원)"
 * ✏️ 10/16 화면 점검 — 감시 행 값을 "1단" 이라 부르면 사다리 ★ 1단과 한 줄에 두 번 겹친다 · 저점매수 탭(BandPanel)이 같은 값을 "매수 기준가" 로 부른다
 *
 * 🔴 "범위 n%" 는 그 기간 저가 최저 ~ 고가 최고 사이 위치다(사다리 안내 식) — 저점매수 매수선(종가 하위 20%)과 다른 잣대다.
 *   그래서 저점매수 쪽은 감시 행의 기준가를 따로 적는다. 기간은 거래일 ÷ 20 을 달로 부른다(LadderGuideSection 과 같다).
 *
 * @returns 계산할 수 없으면 이유 한 줄
 */
export function holdingGuideLine(
  guide: { available: boolean; reason: string | null; positions: { days: number; positionPct: number }[]; baseRung: number | null; band: { bandPrice: number; stopPrice: number } | null },
  formatPrice: (value: number) => string,
): string {
  const parts: string[] = [];
  if (!guide.available) {
    parts.push(guide.reason ?? '기준선 없음');
  } else {
    const ranges = [...guide.positions].sort((a, b) => b.days - a.days);
    ranges.forEach((p, i) => {
      const months = `${Math.round(p.days / 20)}개월`;
      parts.push(i === 0 ? `${months} 범위 ${Math.round(p.positionPct)}%` : `${months} ${Math.round(p.positionPct)}%`);
    });
    if (guide.baseRung !== null) {
      parts.push(`★ 1단 ${formatPrice(guide.baseRung)}`);
    }
  }
  if (guide.band) {
    parts.push(`모의 저점매수 감시 중(매수 기준가 ${formatPrice(guide.band.bandPrice)} · 손절선 ${formatPrice(guide.band.stopPrice)})`);
  }
  return parts.join(' · ');
}

/** 조건주문 종류 (2026-10-16, docs/impl/150 4번) — 모르는 값은 그대로 */
export function conditionalTypeLabel(type: string): string {
  switch (type) {
    case 'SINGLE':
      return '한 조건';
    case 'OCO':
      return '둘 중 하나(OCO)';
    case 'OTO':
      return '이어서(OTO)';
    default:
      return type;
  }
}

/** 조건주문 상태 (진행 중 넷 + 끝 둘 · leg 전용 둘) — 모르는 값은 그대로 */
export function conditionalStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    WATCHING: '감시 중',
    PAUSED: '일시중지',
    ORDERING: '주문 내는 중',
    ORDERED: '주문 냄',
    COMPLETED: '끝남',
    EXPIRED: '만료',
    HOLDING: '앞 조건 대기',
    CANCELED: '취소됨',
  };
  return labels[status] ?? status;
}

/**
 * 감시 조건 한 줄 — "65,000원에 닿으면 매도 · 지정가 64,900원" (2026-10-16, docs/impl/150 4번).
 *
 * 🔴 PROFIT_RATE 는 가격이 아니라 수익률(%)에 닿을 때 낸다 — 가격 칸이 비어 올 수 있다(스펙).
 * 시장가면 주문가가 없다 — "시장가" 로 적는다. 가격을 0 으로 채우지 않는다.
 *
 * @param formatPrice 통화에 맞는 가격 글자(원 · 달러) — 화면이 넘긴다
 */
export function conditionLegLabel(
  leg: { type: string; orderSide: string; triggerPrice: number | null; targetProfitRate: number | null; orderPrice: number | null },
  formatPrice: (value: number) => string,
): string {
  const side = leg.orderSide === 'BUY' ? '매수' : leg.orderSide === 'SELL' ? '매도' : leg.orderSide;
  const trigger =
    leg.type === 'PROFIT_RATE' && leg.targetProfitRate !== null
      ? `수익률 ${leg.targetProfitRate > 0 ? '+' : ''}${leg.targetProfitRate}%에 닿으면`
      : leg.triggerPrice !== null
        ? `${formatPrice(leg.triggerPrice)}에 닿으면`
        : '조건에 닿으면';
  const order = leg.orderPrice !== null ? `지정가 ${formatPrice(leg.orderPrice)}` : '시장가';
  return `${trigger} ${side} · ${order}`;
}

/**
 * 수수료 소수 비율을 퍼센트 글자로 — 0.00015 → "0.015%" · 0.001 → "0.1%" (2026-10-16, docs/impl/150 2번).
 * 0.00015 × 100 이 부동소수로 0.015000000000000001 이 되므로 넷째 자리에서 자르고 뒤 0 을 지운다.
 */
export function feePercent(rate: number): string {
  return `${Number((rate * 100).toFixed(4))}%`;
}

/**
 * 동기화 실패 안내를 띄울지. 실패 시각이 마지막 동기화보다 늦을 때만이다 (2026-10-16, docs/impl/150 1번).
 * 실패 뒤에 배치가 성공했으면 숫자는 다시 최신이다 — 그때도 안내가 남으면 "늘 실패" 로 읽힌다.
 */
export function isSyncFailureCurrent(lastSyncedAt: string | null, syncFailedAt: string | null): boolean {
  if (syncFailedAt === null) {
    return false;
  }
  return lastSyncedAt === null || syncFailedAt > lastSyncedAt;
}

/** 매수는 붉은색, 매도는 파란색. 국내 증권사 관행을 따른다 */
export function sideLabel(side: TradeOrder['side']): string {
  return side === 'BUY' ? '매수' : '매도';
}

export function sideTone(side: TradeOrder['side']): BadgeTone {
  return side === 'BUY' ? 'danger' : 'info';
}

/**
 * 토스 주문 상태의 한국어 이름.
 *
 * 표에 없는 값은 원문 그대로 보여준다.
 *   토스가 상태를 추가할 수 있다. 모르는 값을 "알 수 없음" 으로 뭉개면
 *   무슨 일이 있었는지 확인할 길이 사라진다. 영문이라도 보이는 편이 낫다.
 */
const STATUS_LABEL: Record<string, string> = {
  PENDING: '접수 대기',
  ACCEPTED: '접수됨',
  PARTIALLY_FILLED: '부분 체결',
  FILLED: '체결 완료',
  CANCELED: '취소됨',
  REJECTED: '거부됨',
  REPLACED: '정정됨',
  EXPIRED: '기간 만료',
  CANCEL_REJECTED: '취소 거부',
  REPLACE_REJECTED: '정정 거부',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

/**
 * 주문 한 건의 배지 색.
 *
 * 🔴 status 만으로 정하지 않는다
 *   취소·거부된 주문에도 부분 체결이 남아 있을 수 있다. 스펙이 그렇게 명시한다.
 *   그것을 회색 "취소됨" 으로만 보여주면 실제로 체결된 거래가 화면에서 사라진다.
 *   체결이 있으면 상태와 무관하게 초록으로 올린다.
 */
export function statusTone(order: TradeOrder): BadgeTone {
  if (order.filled) {
    return order.status === 'FILLED' ? 'success' : 'warning';
  }
  if (order.status === 'REJECTED' || order.rejectionRecord) {
    return 'danger';
  }
  return 'neutral';
}

/**
 * 스냅샷이 며칠 전인지 사람 말로.
 *
 * 🔴 이 문구가 화면에서 가장 중요한 한 줄일 수 있다
 *   주말이나 연휴에 열면 금요일 값이 그대로 떠 있다.
 *   그것을 오늘 값으로 읽으면 판단이 며칠 밀린다.
 *
 * 🔴 2026-09-20 (I-76) — "평일 16:00 에만 돈다" 는 전제가 깨졌다
 *   지금은 화면을 볼 때 60초마다 · 확정 스냅샷(평일 16:00·20:10, 매일 06:10) 둘이다.
 *   (9/28 I-78 2차 - "주문 직후(refreshNow)" 도 적혀 있었지만 부르는 곳이 0 이다. 주문 클라이언트가 없다 - BACKLOG I-76)
 *   06:10 은 주말에도 돌아 토요일 아침에는 미국 금요일 장 체결이 들어온다. 그 밖의 주말 · 연휴에는
 *   체결이 없어 값이 금요일 그대로이므로 이 안내는 계속 필요하다. (9/28 I-78 2차)
 *
 * @param snapshotDate YYYY-MM-DD. 없으면 null
 * @param today        비교 기준일. 테스트에서 고정하기 위해 받는다
 */
export function snapshotAge(snapshotDate: string | null, today: Date): string | null {
  const days = daysSince(snapshotDate, today);

  if (days === null) {
    return null;
  }
  if (days <= 0) {
    return '오늘';
  }
  if (days === 1) {
    return '어제';
  }
  return `${days}일 전`;
}

/**
 * 오래된 스냅샷인지. 화면이 경고를 띄울지 정한다.
 *
 * 3일로 잡는다. 금요일 마지막 동기화 값을 월요일 장 시작 전에 보면 3일째다.
 * 그것은 정상이므로 경고하지 않는다. 4일째부터는 동기화가 멈춘 것을 의심해야 한다.
 */
export function isSnapshotStale(snapshotDate: string | null, today: Date): boolean {
  const days = daysSince(snapshotDate, today);
  return days !== null && days > 3;
}

/**
 * 기준일로부터 며칠이 지났는지.
 *
 * 🔴 날짜만 비교한다
 *   시각이 섞이면 같은 날인데도 하루 차이로 나온다.
 *   그리고 'YYYY-MM-DD' 를 그대로 new Date() 에 넣으면 UTC 자정으로 해석되어
 *   KST 아침에는 하루가 밀린다. 숫자로 쪼개 로컬 자정으로 만든다.
 *
 * @returns 날짜가 없거나 형식이 아니면 null
 */
function daysSince(date: string | null, today: Date): number | null {
  if (!date) {
    return null;
  }

  const parts = date.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return null;
  }

  const [year, month, day] = parts as [number, number, number];
  const target = new Date(year, month - 1, day);
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return Math.round((base.getTime() - target.getTime()) / 86_400_000);
}

/**
 * 세금·수수료가 갉아먹은 금액.
 *
 * 평가금액과 순평가금액의 차이다. 단타는 회전이 잦아 이 값이 누적되면 체감된다.
 * 둘 중 하나라도 없으면 계산하지 않는다 - 0 으로 채우면 "비용 없음" 으로 읽힌다.
 */
export function costGap(marketValue: number | null, marketValueNet: number | null): number | null {
  if (marketValue === null || marketValueNet === null) {
    return null;
  }
  return marketValue - marketValueNet;
}
